/**
 * TanStack Query hooks for the Shadow AI Discovery feature.
 *
 * The scan-status query polls via `refetchInterval` while the scan is
 * queued/running and stops automatically on a terminal status. Mutations
 * invalidate in `onSettled` (runs on both success and error) rather than
 * doing optimistic `setQueryData` in `onMutate` — the latter races the
 * refetch in this codebase (see project memory / LLMScanResultsPage notes).
 *
 * IDs are numbers (the backend uses integer PKs). Connections come back
 * paginated; `useAwsDiscoveryConnections` unwraps to the `data` array.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { awsDiscoveryService } from "@/services/awsDiscoveryService";
import type {
  AssetFilters,
  AwsDiscoveryConnection,
  AwsDiscoveryConnectionCreate,
  AwsDiscoverySetupInstructions,
  DiscoveredAssetDetail,
  DiscoveryScan,
  PaginatedAssets,
} from "@/types/awsDiscovery";
import { isTerminalScanStatus } from "@/types/awsDiscovery";

const QK = {
  setup: ["aws-discovery", "setup-instructions"] as const,
  connections: ["aws-discovery", "connections"] as const,
  scan: (scanId: number) => ["aws-discovery", "scan", scanId] as const,
  assets: (filters?: AssetFilters) => ["aws-discovery", "assets", filters] as const,
  asset: (assetId: number) => ["aws-discovery", "asset", assetId] as const,
};

/**
 * Fetch the connect-wizard setup instructions (ExternalId, CloudFormation,
 * console steps). Only fetched when `enabled` is true so the ExternalId is
 * minted lazily when the user opens the wizard.
 */
export function useAwsDiscoverySetupInstructions(enabled: boolean) {
  return useQuery<AwsDiscoverySetupInstructions>({
    queryKey: QK.setup,
    queryFn: () => awsDiscoveryService.getSetupInstructions(),
    enabled,
    staleTime: 0,
  });
}

/**
 * List the org's discovery connections (unwrapped from the paginated response).
 */
export function useAwsDiscoveryConnections() {
  return useQuery<AwsDiscoveryConnection[]>({
    queryKey: QK.connections,
    queryFn: async () => (await awsDiscoveryService.listConnections()).data,
    staleTime: 30000,
  });
}

/**
 * Register a new connection. The backend runs the AssumeRole handshake; on
 * failure the thrown ApiError carries the verbatim AWS message for the UI.
 */
export function useCreateAwsDiscoveryConnection() {
  const queryClient = useQueryClient();

  return useMutation<AwsDiscoveryConnection, Error, AwsDiscoveryConnectionCreate>({
    mutationFn: (data) => awsDiscoveryService.createConnection(data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QK.connections });
    },
  });
}

/**
 * Soft-delete a connection.
 */
export function useDeleteAwsDiscoveryConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => awsDiscoveryService.deleteConnection(connectionId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QK.connections });
      queryClient.invalidateQueries({ queryKey: ["aws-discovery", "assets"] });
    },
  });
}

/**
 * Trigger a discovery scan for a connection. Returns 409 (surfaced as an
 * ApiError) if a scan is already running.
 */
export function useTriggerAwsDiscoveryScan() {
  const queryClient = useQueryClient();

  return useMutation<DiscoveryScan, Error, number>({
    mutationFn: (connectionId) => awsDiscoveryService.triggerScan(connectionId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QK.connections });
    },
  });
}

/**
 * Poll a scan's status. While the status is queued/running this refetches on
 * an interval; it stops once the scan reaches a terminal status. On terminal,
 * the asset list is invalidated so the inventory reflects the new scan.
 */
export function useAwsDiscoveryScan(
  scanId: number | null,
  options?: { pollIntervalMs?: number }
) {
  const queryClient = useQueryClient();
  const pollIntervalMs = options?.pollIntervalMs ?? 3000;

  return useQuery<DiscoveryScan>({
    queryKey: QK.scan(scanId ?? 0),
    queryFn: async () => {
      const scan = await awsDiscoveryService.getScan(scanId as number);
      if (isTerminalScanStatus(scan.status)) {
        // Refresh inventory once the scan finishes.
        queryClient.invalidateQueries({ queryKey: ["aws-discovery", "assets"] });
      }
      return scan;
    },
    enabled: scanId !== null,
    refetchInterval: (query) => {
      const data = query.state.data as DiscoveryScan | undefined;
      return isTerminalScanStatus(data?.status) ? false : pollIntervalMs;
    },
  });
}

/**
 * Fetch the paginated, filterable asset inventory.
 */
export function useAwsDiscoveryAssets(filters?: AssetFilters) {
  return useQuery<PaginatedAssets>({
    queryKey: QK.assets(filters),
    queryFn: () => awsDiscoveryService.listAssets(filters),
    staleTime: 15000,
  });
}

/**
 * Fetch a single asset's detail (including the scan-capability gate).
 */
export function useAwsDiscoveryAsset(assetId: number | null) {
  return useQuery<DiscoveredAssetDetail>({
    queryKey: QK.asset(assetId ?? 0),
    queryFn: () => awsDiscoveryService.getAsset(assetId as number),
    enabled: assetId !== null,
  });
}
